import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Copy, Users } from "lucide-react";

export default function ProjectMembers({ open, onOpenChange, projectId, projectName }) {
  const [members, setMembers] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/members`);
      setMembers(data.members || []);
      setCanManage(!!data.can_manage);
    } catch { toast.error("Could not load project members"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) { setLastInviteUrl(null); load(); } }, [open, projectId]); // eslint-disable-line

  const invite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    setLastInviteUrl(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/members/invite`, { email: email.trim(), name: name.trim() });
      if (data.mode === "added") {
        toast.success(`${data.email} added to the project${data.email_sent ? " — notification sent" : ""}`);
      } else if (data.email_sent) {
        toast.success(`Invite email sent to ${data.email}`);
      } else {
        toast.success(`${data.email} invited — share the invite link with them`);
        setLastInviteUrl(data.invite_url);
      }
      setEmail(""); setName("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not invite that person"); }
    finally { setInviting(false); }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/projects/${projectId}/members/${m.id}`);
      toast.success(`${m.name || m.email} removed from the project`);
      setMembers((prev) => prev.filter((x) => x.id !== m.id));
    } catch { toast.error("Could not remove that member"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="project-members-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Project team</DialogTitle>
          <DialogDescription>
            Members get full read access to {projectName ? <strong>{projectName}</strong> : "this project"} and the Orchestra Pit, and can update only the tasks assigned to them.
          </DialogDescription>
        </DialogHeader>

        {canManage && (
          <div className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Invite a teammate</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="member-invite-name" />
              <Input placeholder="email@company.com" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") invite(); }} data-testid="member-invite-email" />
              <Button onClick={invite} disabled={inviting || !email.trim()} className="gap-1.5 shrink-0" data-testid="member-invite-submit">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Invite
              </Button>
            </div>
            {lastInviteUrl && (
              <div className="flex items-center gap-2 text-xs bg-background rounded-md border border-border px-2 py-1.5">
                <span className="truncate flex-1 text-muted-foreground" data-testid="member-invite-link">{lastInviteUrl}</span>
                <button className="text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                  onClick={() => { navigator.clipboard.writeText(lastInviteUrl); toast.success("Invite link copied"); }}
                  data-testid="member-invite-copy">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="member-list">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : members.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="member-list-empty">No members yet — invite teammates so they can follow the plan and chat in the Pit.</p>
          ) : members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2" data-testid={`member-row-${m.id}`}>
              <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0">
                {(m.name || m.email || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{m.name || m.email}</p>
                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
              </div>
              {m.status === "invited" && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0" data-testid={`member-invited-badge-${m.id}`}>Invited</span>}
              {canManage && (
                <button className="text-muted-foreground hover:text-destructive shrink-0" onClick={() => remove(m)}
                  title="Remove from project" data-testid={`member-remove-${m.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
